import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarCheck, CalendarPlus, ChevronDown, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  calculateDeadline,
  formatBr,
  isValidDate,
  type DeadlineMode,
  type NonWorkingDay,
  type StartKind,
} from '@/lib/deadlineCalc';
import { COURTS, COURT_GROUPS, courtGroupOf, hasRecifeDays } from '@/lib/courtCalendars';

export interface DeadlinePreset {
  days?: number | null;
  mode?: DeadlineMode;
  description?: string;
  caseNumber?: string | null;
  nonce: number;
}

const CUSTOM_DAYS_KEY = 'deadline-custom-days';
const COURT_KEY = 'deadline-court';

const readCourt = () => {
  try {
    const saved = localStorage.getItem(COURT_KEY);
    return saved && COURTS.some((c) => c.code === saved) ? saved : 'TJPE';
  } catch {
    return 'TJPE';
  }
};
const saveCourt = (court: string) => {
  try {
    localStorage.setItem(COURT_KEY, court);
  } catch {
    // Sem armazenamento no navegador: volta ao padrão na próxima visita
  }
};
const WEEK = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const weekDayName = (date: string) => WEEK[new Date(`${date}T12:00:00Z`).getUTCDay()];
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const digits = (value?: string | null) => String(value || '').replace(/\D/g, '');

const readCustomDays = (): NonWorkingDay[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(CUSTOM_DAYS_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((d) => isValidDate(d?.date)) : [];
  } catch {
    return [];
  }
};
const saveCustomDays = (days: NonWorkingDay[]) => {
  try {
    localStorage.setItem(CUSTOM_DAYS_KEY, JSON.stringify(days));
  } catch {
    // Sem armazenamento no navegador: vale só nesta sessão
  }
};

export const DeadlineCalculator = ({ preset }: { preset?: DeadlinePreset }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  const [baseDate, setBaseDate] = useState(today);
  const [startKind, setStartKind] = useState<StartKind>('disponibilizacao');
  const [days, setDays] = useState('15');
  const [mode, setMode] = useState<DeadlineMode>('uteis');
  const [court, setCourtState] = useState(readCourt);
  const setCourt = (value: string) => {
    setCourtState(value);
    saveCourt(value);
  };
  const [doubled, setDoubled] = useState(false);
  const [capitalRecife, setCapitalRecife] = useState(false);
  const [suspendInRecess, setSuspendInRecess] = useState(true);
  const [customDays, setCustomDays] = useState<NonWorkingDay[]>(readCustomDays);
  const [newDay, setNewDay] = useState('');
  const [newReason, setNewReason] = useState('');
  const [showSkipped, setShowSkipped] = useState(false);

  const [cases, setCases] = useState<{ id: string; title: string; case_number_cnj: string | null }[]>([]);
  const [caseId, setCaseId] = useState('none');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from('cases')
        .select('id, title, case_number_cnj')
        .or('archived.is.null,archived.eq.false')
        .order('title');
      setCases(data || []);
    })();
  }, []);

  useEffect(() => {
    if (!preset) return;
    if (preset.days) setDays(String(preset.days));
    if (preset.mode) setMode(preset.mode);
    if (preset.description) setDescription(preset.description);
  }, [preset]);

  useEffect(() => {
    if (!preset?.caseNumber || !cases.length) return;
    const found = cases.find((c) => digits(c.case_number_cnj) === digits(preset.caseNumber));
    if (found) setCaseId(found.id);
  }, [preset, cases]);

  const { result, error } = useMemo(() => {
    try {
      return {
        result: calculateDeadline({ baseDate, startKind, days: Number(days), mode, doubled, court, capitalRecife, suspendInRecess, customDays }),
        error: null,
      };
    } catch (e) {
      return { result: null, error: (e as Error).message };
    }
  }, [baseDate, startKind, days, mode, doubled, court, capitalRecife, suspendInRecess, customDays]);

  const addCustomDay = () => {
    if (!isValidDate(newDay)) return;
    const next = [...customDays.filter((d) => d.date !== newDay), { date: newDay, reason: newReason.trim() || 'Sem expediente' }].sort((a, b) => a.date.localeCompare(b.date));
    setCustomDays(next);
    saveCustomDays(next);
    setNewDay('');
    setNewReason('');
  };
  const removeCustomDay = (date: string) => {
    const next = customDays.filter((d) => d.date !== date);
    setCustomDays(next);
    saveCustomDays(next);
  };

  const createTask = async () => {
    if (!result || !user) return;
    setSaving(true);
    try {
      const label = description.trim() || `Prazo de ${result.countedDays} dias`;
      const memory = [
        `${startKind === 'disponibilizacao' ? 'Disponibilização' : 'Publicação/intimação'}: ${formatBr(baseDate)}`,
        startKind === 'disponibilizacao' ? `Publicação: ${formatBr(result.publication)}` : '',
        `Início da contagem: ${formatBr(result.start)}`,
        `${result.countedDays} dias ${mode === 'uteis' ? 'úteis' : 'corridos'}${doubled ? ' (em dobro)' : ''}`,
        `Vencimento: ${formatBr(result.end)}`,
        'Calculado pela Calculadora de Prazos. Confira no calendário do tribunal.',
      ].filter(Boolean).join('\n');
      const { error: insertError } = await (supabase as any).from('tasks').insert({
        user_id: user.id,
        case_id: caseId === 'none' ? null : caseId,
        title: `Prazo: ${label}`.slice(0, 200),
        description: memory,
        status: 'Pendente',
        priority: 'Alta',
        due_date: `${result.end}T12:00:00.000Z`,
        court_deadline: true,
        tags: ['prazo-calculado'],
      });
      if (insertError) throw insertError;
      toast({ title: 'Prazo salvo nas tarefas', description: `Vence em ${formatBr(result.end)}.` });
    } catch (e) {
      toast({ title: 'Não foi possível salvar', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]">
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="space-y-1.5">
            <Label>O que aconteceu</Label>
            <Select value={startKind} onValueChange={(v) => setStartKind(v as StartKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="disponibilizacao">Disponibilização no Diário</SelectItem>
                <SelectItem value="publicacao">Publicação ou intimação</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deadline-date">Data</Label>
            <Input id="deadline-date" type="date" value={baseDate} onChange={(e) => setBaseDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deadline-days">Prazo (dias)</Label>
            <Input id="deadline-days" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Contagem</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as DeadlineMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="uteis">Dias úteis (cível, juizados)</SelectItem>
                <SelectItem value="corridos">Dias corridos (penal)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 xl:col-span-2">
            <Label>Tribunal</Label>
            <Select value={court} onValueChange={setCourt}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {COURT_GROUPS.map((group) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {COURTS.filter((c) => c.group === group).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-4">
          <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
            <span>Prazo em dobro <span className="text-slate-400">(Fazenda Pública, MP, Defensoria)</span></span>
            <Switch checked={doubled} onCheckedChange={setDoubled} />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
            <span>Suspender no recesso <span className="text-slate-400">(20/12 a 20/01)</span></span>
            <Switch checked={suspendInRecess} onCheckedChange={setSuspendInRecess} />
          </label>
          {hasRecifeDays(court) && (
            <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Processo no Recife <span className="text-slate-400">(16/07, feriado municipal)</span></span>
              <Switch checked={capitalRecife} onCheckedChange={setCapitalRecife} />
            </label>
          )}
        </div>

        <div className="space-y-2 border-t border-slate-100 pt-4">
          <Label>Outros dias sem expediente</Label>
          <p className="text-xs text-slate-500">Feriado do município ou portaria do tribunal. Fica salvo neste navegador.</p>
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={newDay} onChange={(e) => setNewDay(e.target.value)} className="w-40" aria-label="Data sem expediente" />
            <Input value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="Motivo" className="min-w-32 flex-1" />
            <Button type="button" variant="outline" size="icon" onClick={addCustomDay} disabled={!isValidDate(newDay)} aria-label="Adicionar dia">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {customDays.length > 0 && (
            <ul className="space-y-1">
              {customDays.map((d) => (
                <li key={d.date} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                  <span>{formatBr(d.date)} · {d.reason}</span>
                  <button type="button" onClick={() => removeCustomDay(d.date)} aria-label={`Remover ${formatBr(d.date)}`} className="text-slate-400 hover:text-slate-700">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-[#c4ab83]/50 bg-[#c4ab83]/10 p-5">
          {result ? (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7d6640]">Vence em</p>
              <p className="mt-1 text-4xl font-semibold tracking-tight text-slate-900">{formatBr(result.end)}</p>
              <p className="text-sm capitalize text-slate-600">{weekDayName(result.end)}</p>
              <dl className="mt-4 space-y-1.5 border-t border-[#c4ab83]/30 pt-3 text-sm">
                {startKind === 'disponibilizacao' && (
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Publicação</dt><dd className="font-medium text-slate-800">{formatBr(result.publication)}</dd></div>
                )}
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Início da contagem</dt><dd className="font-medium text-slate-800">{formatBr(result.start)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Prazo contado</dt><dd className="font-medium text-slate-800">{result.countedDays} dias {mode === 'uteis' ? 'úteis' : 'corridos'}</dd></div>
              </dl>
              {result.movedEndFrom && (
                <p className="mt-3 text-xs text-slate-600">O prazo terminaria em {formatBr(result.movedEndFrom)}, dia sem expediente, e passou para o próximo dia útil.</p>
              )}
              {result.skipped.length > 0 && (
                <div className="mt-3">
                  <button type="button" onClick={() => setShowSkipped((v) => !v)} className="flex items-center gap-1 text-xs font-medium text-[#7d6640]">
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showSkipped ? 'rotate-180' : ''}`} />
                    {result.skipped.length} {result.skipped.length === 1 ? 'dia não contado' : 'dias não contados'}
                  </button>
                  {showSkipped && (
                    <ul className="mt-2 max-h-52 space-y-0.5 overflow-y-auto text-xs text-slate-600">
                      {result.skipped.map((s) => <li key={s.date}>{formatBr(s.date)} · {s.reason}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-600">{error}</p>
          )}
        </div>

        {result && result.calendars.some((c) => !c.source) && (
          <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <strong className="font-semibold">Sem o calendário oficial do {court} para {result.calendars.filter((c) => !c.source).map((c) => c.year).join(' e ')}.</strong>{' '}
              O cálculo usa só os feriados da lei nacional{courtGroupOf(court) === 'Justiça Federal' ? ' e da Lei 5.010/66' : ''}. A data real pode ser mais tarde: inclua os feriados e suspensões do tribunal em "Outros dias sem expediente".
            </p>
          </div>
        )}

        {result && result.calendars.some((c) => c.source) && (
          <p className="flex items-start gap-1.5 text-xs text-slate-600">
            <CalendarCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#9a7f52]" />
            <span>Calendário oficial aplicado: {result.calendars.filter((c) => c.source).map((c) => `${c.source} (${c.year})`).join('; ')}.</span>
          </p>
        )}

        {courtGroupOf(court) === 'Justiça Eleitoral' && (
          <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Em período eleitoral, os prazos correm também em fins de semana e feriados (Lei 9.504/97, art. 16). Nesse caso, confira as regras do tribunal.</p>
          </div>
        )}

        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Confira o resultado antes de usar. O cálculo não conhece feriados do município da comarca nem suspensões por portaria. Informe esses dias ao lado.</p>
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarCheck className="h-4 w-4 text-[#9a7f52]" />Salvar como tarefa</p>
          <div className="space-y-1.5">
            <Label htmlFor="deadline-description">O que precisa ser feito</Label>
            <Input id="deadline-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Contestação" />
          </div>
          <div className="space-y-1.5">
            <Label>Processo</Label>
            <Select value={caseId} onValueChange={setCaseId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem processo</SelectItem>
                {cases.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.title?.slice(0, 60)}{c.case_number_cnj ? ` · ${c.case_number_cnj}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={createTask} disabled={!result || saving} className="w-full bg-slate-900 text-white hover:bg-slate-800">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarPlus className="mr-2 h-4 w-4 text-[#e8d5a8]" />}
            Criar tarefa com vencimento{result ? ` em ${formatBr(result.end)}` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};
